import { NodeProps } from "@xyflow/react";
import { useWorkflowSelectedGroupId } from "../../context/workflow-selected-group-context";
import { useWorkflowsEditorWorkflows } from "../../context/workflows-editor-workflows-context";
import { useTranslation } from "react-i18next";
import { Copy, SquarePen, Trash2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@babackapp/ui/components/alert-dialog";
import { useCallback, useState } from "react";
import useFocusNode from "../../hooks/use-focus-node";
import { Switch } from "@babackapp/ui/components/switch";
import { cn } from "@babackapp/ui/lib/utils";


export type WorkflowGroupNodeData = {
  id: string;
  position: { x: number; y: number };
  data: {
    label: string;
    workflowId: string;
    isActive: boolean;
  }
};


export function WorkflowGroupNode({ data, id }: NodeProps<WorkflowGroupNodeData>) {
  const { selectedGroupId, setSelectedGroupId } = useWorkflowSelectedGroupId();
  const {
    workflows,
    removeWorkflow,
    addWorkflow,
    onWorkflowSelect,
  } = useWorkflowsEditorWorkflows();
  const { t } = useTranslation("trackoffice");
  const [dialogState, setDialogState] = useState<"delete" | "enable" | "disable" | null>(null);
  const workflow = workflows.find(w => w.id === data.workflowId);
  const { focusNode } = useFocusNode();
  const { toggleWorkflow, isUpdating } = useWorkflowToggle(data.workflowId);

  const isSelected = selectedGroupId === id;

  const handleClick = () => {
    setSelectedGroupId(id);
    focusNode(id, { xOffset: -50 });
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (workflow) {
      onWorkflowSelect?.(workflow.id);
    }
  };

  const handleDuplicate = async () => {
    if (workflow) {
      await addWorkflow(workflow);
    }
  };

  const handleDelete = () => {
    removeWorkflow(data.workflowId);
    setDialogState(null);
  };

  const handleSwitchChange = (checked: boolean) => {
    setDialogState(checked ? "enable" : "disable");
  };

  const handleConfirmToggle = async () => {
    const newState = dialogState === "enable";
    await toggleWorkflow(newState);
    setDialogState(null);
  };

  const getDialogConfig = () => {
    switch (dialogState) {
      case "delete":
        return {
          title: t("workflowsEditor.deleteWorkflowDialog.title", "Delete workflow?"),
          description: t(
            "workflowsEditor.deleteWorkflowDialog.description",
            "Are you sure you want to delete this workflow? This action cannot be undone.",
          ),
          cancelText: t("workflowsEditor.deleteWorkflowDialog.cancel", "Cancel"),
          confirmText: t("workflowsEditor.deleteWorkflowDialog.confirm", "Delete"),
          onConfirm: handleDelete,
        };
      case "enable":
        return {
          title: t("workflowsEditor.enableWorkflowDialog.title", "Enable workflow?"),
          description: t(
            "workflowsEditor.enableWorkflowDialog.description",
            "Are you sure you want to enable this workflow? It will start running automatically.",
          ),
          cancelText: t("workflowsEditor.enableWorkflowDialog.cancel", "Cancel"),
          confirmText: t("workflowsEditor.enableWorkflowDialog.confirm", "Enable"),
          onConfirm: handleConfirmToggle,
        };
      case "disable":
        return {
          title: t("workflowsEditor.disableWorkflowDialog.title", "Disable workflow?"),
          description: t(
            "workflowsEditor.disableWorkflowDialog.description",
            "Are you sure you want to disable this workflow? It will stop running automatically.",
          ),
          cancelText: t("workflowsEditor.disableWorkflowDialog.cancel", "Cancel"),
          confirmText: t("workflowsEditor.disableWorkflowDialog.confirm", "Disable"),
          onConfirm: handleConfirmToggle,
        };
      default:
        return null;
    }
  };

  const dialogConfig = getDialogConfig();

  return (
    <>
      <div onClick={handleClick} className="h-full w-full relative">
        <div className="absolute top-0 left-0 font-bold text-xs z-10 flex items-center gap-2" style={{
          transform: "translateY(-24px)",
        }}>
          <Switch
            checked={data.isActive}
            onCheckedChange={handleSwitchChange}
            disabled={isUpdating}
            onClick={(e) => e.stopPropagation()}
            className="scale-75"
          />
          {data.label}
        </div>
        <div
          className={cn(
            "h-full w-full relative flex items-start justify-start p-4 rounded-lg cursor-pointer transition-all duration-300 ease-in-out overflow-hidden border",
            data.isActive ? "border-purple-600 bg-purple-600/10 hover:bg-opacity-100" : "border-gray-200 bg-gray-200/10 hover:bg-opacity-100",
          )}
          style={{
            transform: isSelected ? "scale(1.02)" : "scale(1)",
          }}
        >
        </div>
        <ActionsMenu
          isSelected={isSelected}
          onEdit={handleEdit}
          onDuplicate={handleDuplicate}
          onDelete={() => setDialogState("delete")}
          editText={t("workflowsEditor.workflowsList.editWorkflow", "Edit")}
          duplicateText={t("workflowsEditor.workflowsList.duplicateWorkflow", "Duplicate")}
          deleteText={t("workflowsEditor.workflowsList.deleteWorkflow", "Delete")}
        />
      </div>
      {dialogConfig && (
        <ConfirmDialog
          open={dialogState !== null}
          onOpenChange={(open) => !open && setDialogState(null)}
          {...dialogConfig}
        />
      )}
    </>
  );
}


function useWorkflowToggle(workflowId: string) {
  const { updateWorkflow } = useWorkflowsEditorWorkflows();
  const [isUpdating, setIsUpdating] = useState(false);

  const toggleWorkflow = useCallback(async (isActive: boolean) => {
    setIsUpdating(true);
    try {
      await updateWorkflow({ isActive }, workflowId);
    } finally {
      setIsUpdating(false);
    }
  }, [workflowId, updateWorkflow]);

  return { toggleWorkflow, isUpdating };
}


interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  cancelText: string;
  confirmText: string;
  onConfirm: () => void;
  variant?: "default" | "destructive";
}


function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  cancelText,
  confirmText,
  onConfirm,
}: ConfirmDialogProps) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{cancelText}</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm}>{confirmText}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}


// Actions menu component
interface ActionsMenuProps {
  isSelected: boolean;
  onEdit: (e: React.MouseEvent) => void;
  onDuplicate: () => void;
  onDelete: () => void;
  editText: string;
  duplicateText: string;
  deleteText: string;
}


function ActionsMenu({
  isSelected,
  onEdit,
  onDuplicate,
  onDelete,
  editText,
  duplicateText,
  deleteText,
}: ActionsMenuProps) {
  return (
    <div
      className="absolute left-0 bottom-0 flex items-center gap-2 transition-all duration-300 ease-in-out mt-4"
      style={{
        zIndex: -1,
        transform: isSelected ? "translateY(140%)" : "translateY(0)",
        opacity: isSelected ? 1 : 0,
        pointerEvents: isSelected ? "auto" : "none",
      }}
    >
      <button
        onClick={onEdit}
        className="bg-white text-purple-600 px-3 py-1.5 rounded-md hover:bg-purple-50 transition-colors shadow-md flex"
      >
        <SquarePen className="mr-2 h-4 w-4" />
        {editText}
      </button>
      <button
        onClick={onDuplicate}
        className="bg-white text-purple-600 px-3 py-1.5 rounded-md hover:bg-purple-50 transition-colors shadow-md flex"
      >
        <Copy className="mr-2 h-4 w-4" />
        {duplicateText}
      </button>
      <button
        onClick={onDelete}
        className="bg-white text-red-600 px-3 py-1.5 rounded-md hover:bg-red-50 transition-colors shadow-md flex"
      >
        <Trash2 className="mr-2 h-4 w-4" />
        {deleteText}
      </button>
    </div>
  );
}
